import { useEffect, useRef, useState } from 'react';
import { type LucideIcon, Pause, Play, Settings, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils/cn';
import { VERB_COLOR, type IvrVerb } from '../types';

interface NodeCardProps {
  verb: IvrVerb;
  icon: LucideIcon;
  label: string;
  summary?: React.ReactNode;
  configurable?: boolean;
  removable?: boolean;
  onConfigure?: () => void;
  onRemove?: () => void;
  children?: React.ReactNode;
  className?: string;
  status?: 'configured' | 'incomplete';
  selected?: boolean;
  audioUrl?: string;
  onPlayClick?: () => void;
}

export function NodeCard({
  verb,
  icon: Icon,
  label,
  summary,
  configurable = true,
  removable = true,
  onConfigure,
  onRemove,
  children,
  className,
  status,
  selected = false,
  audioUrl,
  onPlayClick,
}: NodeCardProps) {
  const v = VERB_COLOR[verb];
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    return () => {
      audioRef.current?.pause();
      audioRef.current = null;
    };
  }, [audioUrl]);

  const togglePlay = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onPlayClick) {
      onPlayClick();
      return;
    }
    if (!audioUrl) return;

    if (isPlaying) {
      audioRef.current?.pause();
      audioRef.current = null;
      setIsPlaying(false);
      return;
    }

    const a = new Audio(audioUrl);
    a.onended = () => setIsPlaying(false);
    a.onerror = () => {
      console.error('Node audio failed to load:', audioUrl);
      setIsPlaying(false);
    };

    a
      .play()
      .then(() => {
        audioRef.current = a;
        setIsPlaying(true);
      })
      .catch((err) => {
        console.error('Node audio playback blocked/failed:', audioUrl, err);
        setIsPlaying(false);
      });
  };

  return (
    <div
      className={cn(
        'group/node relative w-[132px] overflow-hidden rounded-md border bg-card shadow-sm transition-all hover:shadow-md',
        selected
          ? 'border-blue-400 ring-2 ring-blue-300/60 ring-offset-1'
          : 'border-border',
        className,
      )}
    >
      <span
        aria-hidden
        className={cn(
          'absolute inset-y-0 left-0',
          selected ? 'w-1 bg-blue-500' : 'w-[3px]',
          !selected && v.bg,
        )}
      />

      <div className="flex items-center gap-1 pl-1.5 pr-1 py-1">
        <div className="relative shrink-0">
          <div
            className={cn(
              'flex h-5 w-5 items-center justify-center rounded-full text-white shadow-sm',
              v.bg,
            )}
          >
            <Icon className="h-2.5 w-2.5" />
          </div>
          {status && (
            <span
              aria-label={status === 'configured' ? 'Configured' : 'Needs config'}
              className={cn(
                'absolute -bottom-0.5 -right-0.5 h-1.5 w-1.5 rounded-full ring-1 ring-card',
                status === 'configured' ? 'bg-emerald-500' : 'bg-amber-500',
              )}
            />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="text-[8px] font-bold uppercase tracking-wider text-muted-foreground leading-none">
            {label}
          </div>
          {summary !== undefined && (
            <div className="mt-0.5 truncate text-[9px] leading-tight text-foreground">
              {summary}
            </div>
          )}
          {children}
        </div>

        {(configurable || removable || audioUrl || onPlayClick) && (
          <div
            className={cn(
              'flex shrink-0 items-center gap-0.5 transition-opacity',
              selected
                ? 'opacity-100'
                : 'opacity-0 group-hover/node:opacity-100',
            )}
          >
            {(audioUrl || onPlayClick) && (
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="h-4 w-4"
                onClick={togglePlay}
                aria-label={onPlayClick ? 'Listen' : isPlaying ? 'Pause audio' : 'Play audio'}
                title={onPlayClick ? 'Listen' : isPlaying ? 'Pause audio' : 'Play audio'}
              >
                {!onPlayClick && isPlaying ? (
                  <Pause className="h-2.5 w-2.5" />
                ) : (
                  <Play className="h-2.5 w-2.5" />
                )}
              </Button>
            )}
            {configurable && onConfigure && (
              <Button
                size="icon"
                variant="ghost"
                className="h-4 w-4"
                onClick={onConfigure}
                aria-label="Configure"
                title="Configure"
              >
                <Settings className="h-2.5 w-2.5" />
              </Button>
            )}
            {removable && onRemove && (
              <Button
                size="icon"
                variant="ghost"
                className="h-4 w-4 text-destructive hover:text-destructive"
                onClick={onRemove}
                aria-label="Remove"
                title="Remove"
              >
                <Trash2 className="h-2.5 w-2.5" />
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}